import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  Space,
  Modal,
  DatePicker,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "./../../../../../../features/save/saveSlice";
import dayjs from "dayjs";
import { dataTypeData } from "../../../../../../data";

const DataSchemeAdd = ({ isModalOpen, handleCancel, handleFetch, prodd }) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { schemeBlocks } = useSelector((state) => state.fetch);
  const { activeObjSchemes } = useSelector((state) => ({
    activeObjSchemes: state.obj.activeObj["communitySchemes"],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const onFinish = async (data) => {
    data.proId = prodd?.proId ? prodd?.proId : null;
    data.proSchId = activeObjSchemes?.schId;
    data.proBlocks = Array.isArray(data?.proBlocks)
      ? data?.proBlocks.join(",")
      : data?.proBlocks;
    const res = await dispatch(
      save({
        ...data,
        url: "prod/production",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await handleFetch();
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  useEffect(() => {
    if (prodd) {
      form.setFieldsValue({
        ...prodd,
        proStartDate: prodd?.proStartDate
          ? dayjs(new Date(prodd?.proStartDate))
          : null,
        proEndDate: prodd?.proEndDate
          ? dayjs(new Date(prodd?.proEndDate))
          : null,
        proBlocks: prodd?.proBlocks?.split(",")?.map((b) => b?.trim()),
      });
    } else {
      form.resetFields();
    }
  }, [prodd, form]);
  return (
    <Modal
      title={prodd?.proId ? "Update a data schema" : "Create a data schema"}
      open={isModalOpen}
      onOk={onFinish}
      onCancel={handleCancel}
      className="!w-[43.3125rem]"
      maskClosable={false}
    >
      <Form
        layout="vertical"
        ref={formRef}
        name="control-ref"
        onFinish={onFinish}
        className=""
        style={{
          maxWidth: "100%",
        }}
        form={form}
      >
        <Form.Item
          name="proType"
          className=""
          label={"Data type"}
          rules={[
            {
              required: true,
              message: "Please data type",
            },
          ]}
        >
          <Select
            placeholder="Select"
            className=""
            allowClear
            style={{
              width: "100%",
            }}
            options={
              dataTypeData?.length > 0 &&
              dataTypeData?.map((item) => ({
                value: item?.value,
                label: item?.label,
              }))
            }
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label?.toLocaleLowerCase() ?? "").includes(
                input?.toLocaleLowerCase()
              )
            }
          />
        </Form.Item>
        <Form.Item
          name="proDesc"
          label={"Description"}
          rules={[
            {
              required: true,
              message: "Please add description",
            },
          ]}
        >
          <Input className="input" />
        </Form.Item>

        <div className="grid lg:grid-cols-2 grid-cols-1 gap-x-[1rem]">
          <Form.Item
            name="proStartDate"
            rules={[
              {
                required: true,
                message: "Start period is required",
              },
            ]}
            label="Start Period"
          >
            <DatePicker
              format={"YYYY MMM DD"}
              className="input"
              onChange={(date) => setStartDate(date)}
              disabledDate={(current) =>
                endDate ? current && current?.isAfter(endDate, "day") : false
              }
            />
          </Form.Item>

          <Form.Item
            name="proEndDate"
            rules={[
              {
                required: true,
                message: "End period is required",
              },
            ]}
            label="End Period"
          >
            <DatePicker
              format={"YYYY MMM DD"}
              className="input"
              onChange={(date) => setEndDate(date)}
              disabledDate={(current) =>
                startDate
                  ? current && current?.isBefore(startDate, "day")
                  : false
              }
            />
          </Form.Item>
        </div>

        <Form.Item
          label={"Blocks"}
          name="proBlocks"
          rules={[
            {
              required: true,
              message: "Select blocks",
            },
          ]}
        >
          <Select
            className="!h-full"
            mode="multiple"
            allowClear
            showSearch
            dropdownRender={(menu) => (
              <>
                <Space style={{ padding: "8px 8px 4px" }}>
                  <span className="select-info mt-1">
                    Choose one or more of the following blocks
                  </span>
                </Space>
                <Divider style={{ margin: "8px 0" }} />
                {menu}
              </>
            )}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children?.toLowerCase() ?? "").includes(
                input.toLowerCase()
              )
            }
            tagRender={(props) => {
              const { label, closable, onClose } = props;
              return (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "4px 8px",
                    margin: "2px",
                    borderRadius: "4px",
                    background: "#f5f5f5",
                  }}
                >
                  {label}
                  {closable && (
                    <span
                      onClick={onClose}
                      style={{
                        marginLeft: "8px",
                        cursor: "pointer",
                        color: "red",
                      }}
                    >
                      ×
                    </span>
                  )}
                </span>
              );
            }}
            dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
          >
            {schemeBlocks?.length > 0 &&
              schemeBlocks?.map((item) => (
                <Select.Option key={item?.blocId} value={item?.blocId}>
                  {item?.blocName}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <div className="flex justify-between mt-[48px] ">
          <div className="justify-start"></div>
          <div className="justify-end flex items-center mb-[58px] gap-x-5">
            <div className="w-[150px]">
              <button
                type="button"
                key="back"
                onClick={handleCancel}
                className="cstm-btn"
              >
                Cancel
              </button>
            </div>

            <div className="w-[150px]">
              <button
                disabled={saving}
                key="submit"
                type="submit"
                className="cstm-btn-2"
              >
                {saving ? <Spin /> : prodd?.usrId ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
export default DataSchemeAdd;
